import React from 'react'
import PropTypes from 'prop-types'
import { Alert, Badge, Card, CardBody, Col, Progress, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faExclamationTriangle, faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../../common/Flex'
import { formatNumber, formatPercent, formatUsd } from '../../../rewards/rewardsUtils'
import { formatRatio } from '../rewardsAdminUtils'

const formatHc = (value) => `${formatNumber(value, 2)} HC`

const Kpi = ({ title, value, detail, badge }) => (
  <Card className="h-100">
    <CardBody>
      <h6 className="text-600 fs--1 mb-1">{title}</h6>
      <p className="fs-2 font-weight-bold text-900 mb-1 text-break">{value}</p>
      {badge}
      {detail && <p className="fs--1 text-600 mb-0 mt-1">{detail}</p>}
    </CardBody>
  </Card>
)

const DetailRow = ({ label, value, strong }) => (
  <div className="d-flex justify-content-between align-items-baseline border-bottom border-200 py-2 fs--1">
    <span className="text-700 mr-2">{label}</span>
    <span className={`text-nowrap ${strong ? 'font-weight-semi-bold text-900' : 'text-900'}`}>{value}</span>
  </div>
)

const DetailCard = ({ title, rows }) => (
  <Card className="h-100">
    <CardBody>
      <h5 className="fs-0 mb-2">{title}</h5>
      {rows.map(([label, value, strong]) => (
        <DetailRow key={label} label={label} value={value} strong={strong} />
      ))}
    </CardBody>
  </Card>
)

// El criterio de aceptación pide destacar la cobertura cuando baja del umbral.
const CoverageCard = ({ liability, coins, funds }) => {
  const ratio = liability.coverage_ratio
  const threshold = Number(liability.threshold) || 1
  const progress = ratio === null ? 100 : Math.min(100, (Number(ratio) / threshold) * 100)

  return (
    <Card className={`mb-3 ${liability.covered ? '' : 'border border-danger'}`}>
      <CardBody>
        <Flex justify="between" align="center" className="mb-2 flex-wrap">
          <h5 className="fs-0 mb-1 mr-2">Pasivo en Coins y cobertura del Fondo de Puntos</h5>
          <Badge color={liability.covered ? 'soft-success' : 'soft-danger'} pill className="fs--1">
            <FontAwesomeIcon icon={liability.covered ? faCheckCircle : faExclamationTriangle} className="mr-1" />
            {liability.covered ? 'Cubierto' : 'Bajo el umbral'}
          </Badge>
        </Flex>
        {!liability.covered && (
          <Alert color="danger" className="fs--1">
            El Fondo de Puntos cubre el <b>{formatRatio(ratio)}</b> del pasivo y el mínimo configurado es el{' '}
            <b>{formatRatio(threshold)}</b>. Faltan <b>{formatUsd(liability.shortfall_usd)}</b> para cubrir las
            Coins en circulación.
          </Alert>
        )}
        <Row>
          <Col xs={6} md={3} className="mb-2">
            <h6 className="text-600 fs--1 mb-1">Pasivo en USD</h6>
            <p className="fs-1 font-weight-semi-bold text-900 mb-0">{formatUsd(liability.hc_liability_usd)}</p>
          </Col>
          <Col xs={6} md={3} className="mb-2">
            <h6 className="text-600 fs--1 mb-1">Coins en circulación</h6>
            <p className="fs-1 font-weight-semi-bold text-900 mb-0">{formatHc(coins.hc_in_circulation)}</p>
          </Col>
          <Col xs={6} md={3} className="mb-2">
            <h6 className="text-600 fs--1 mb-1">Saldo del Fondo de Puntos</h6>
            <p className="fs-1 font-weight-semi-bold text-900 mb-0">{formatUsd(funds.points_balance_usd)}</p>
          </Col>
          <Col xs={6} md={3} className="mb-2">
            <h6 className="text-600 fs--1 mb-1">Cobertura</h6>
            <p className={`fs-1 font-weight-semi-bold mb-0 ${liability.covered ? 'text-900' : 'text-danger'}`}>
              {formatRatio(ratio)}
            </p>
          </Col>
        </Row>
        <Progress
          value={progress}
          color={liability.covered ? 'success' : 'danger'}
          className="mt-2"
          style={{ height: 6 }}
          aria-label="Cobertura respecto del umbral"
        />
        <p className="fs--2 text-500 mb-0 mt-1">Umbral mínimo: {formatRatio(threshold)} del pasivo.</p>
      </CardBody>
    </Card>
  )
}

const ReconciliationRow = ({ label, left, right, matches }) => (
  <Row className="border-bottom border-200 py-2 fs--1 align-items-center no-gutters">
    <Col xs={12} md={5} className="text-700 mb-1 mb-md-0">
      <FontAwesomeIcon
        icon={matches ? faCheckCircle : faTimesCircle}
        className={`mr-2 ${matches ? 'text-success' : 'text-danger'}`}
      />
      {label}
    </Col>
    <Col xs={6} md={3} className="text-md-right">
      <span className="text-600 d-md-none">Saldo: </span>
      {left}
    </Col>
    <Col xs={6} md={4} className="text-right">
      <span className="text-600 d-md-none">Ledger: </span>
      {right}
    </Col>
  </Row>
)

const ReconciliationCard = ({ reconciliation }) => (
  <Card className="mb-3">
    <CardBody>
      <Flex justify="between" align="center" className="mb-1 flex-wrap">
        <h5 className="fs-0 mb-1 mr-2">Conciliación</h5>
        <Badge color={reconciliation.balanced ? 'soft-success' : 'soft-danger'} pill className="fs--1">
          {reconciliation.balanced ? 'Todo cuadra' : 'Hay diferencias'}
        </Badge>
      </Flex>
      <p className="fs--1 text-600 mb-2">
        Cada saldo se compara con la suma de los movimientos de su ledger. Una diferencia indica un saldo que se
        desvió de sus movimientos.
      </p>
      <Row className="d-none d-md-flex fs--2 text-600 text-uppercase no-gutters pb-1 border-bottom border-200">
        <Col md={5}>Comparación</Col>
        <Col md={3} className="text-right">Saldo</Col>
        <Col md={4} className="text-right">Suma del ledger</Col>
      </Row>
      <ReconciliationRow
        label="Wallets de los propietarios"
        left={formatHc(reconciliation.wallets_balance_hc)}
        right={formatHc(reconciliation.points_ledger_balance_hc)}
        matches={reconciliation.wallets_match_ledger}
      />
      <ReconciliationRow
        label="Fondo de Puntos"
        left={formatUsd(reconciliation.points_fund_balance_usd)}
        right={formatUsd(reconciliation.points_fund_ledger_usd)}
        matches={reconciliation.points_fund_matches_ledger}
      />
      <ReconciliationRow
        label="Fondo de Premios"
        left={formatUsd(reconciliation.prize_fund_balance_usd)}
        right={formatUsd(reconciliation.prize_fund_ledger_usd)}
        matches={reconciliation.prize_fund_matches_ledger}
      />
    </CardBody>
  </Card>
)

// HO-TASK-289: totales del período según GET /rewards/report (HO-TASK-277).
const ReportSummary = ({ report }) => {
  const { revenue, funds, coins, liability, cost, reconciliation, raffles } = report
  const overTarget = Number(cost.cost_per_transaction_usd) > Number(cost.target_cost_per_transaction_usd)
  const negativeMargin = Number(cost.net_margin_usd) < 0

  return (
    <>
      {report.scope_note && (
        <Alert color="info" className="fs--1">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          Filtrado por condominio: las Coins y los aceleradores son solo de ese condominio, pero los fondos, su
          cobertura y la conciliación son globales de Homelan.
        </Alert>
      )}

      <Row className="mb-3">
        <Col sm={6} xl={3} className="mb-3 mb-xl-0">
          <Kpi
            title="Ingreso por comisión"
            value={formatUsd(revenue.gateway_commission_usd)}
            detail={`${formatNumber(revenue.transactions)} pagos aprobados · ${formatUsd(revenue.collection_usd)} procesados`}
          />
        </Col>
        <Col sm={6} xl={3} className="mb-3 mb-xl-0">
          <Kpi
            title="Costo del programa"
            value={formatUsd(cost.program_cost_usd)}
            detail="Aportes al Fondo de Puntos y al Fondo de Premios."
          />
        </Col>
        <Col sm={6} xl={3} className="mb-3 mb-sm-0">
          <Kpi
            title="Margen neto retenido"
            value={formatUsd(cost.net_margin_usd)}
            badge={
              <Badge color={negativeMargin ? 'soft-danger' : 'soft-success'} pill className="fs--2">
                {formatPercent(cost.net_margin_percent, 2)} de la comisión
              </Badge>
            }
          />
        </Col>
        <Col sm={6} xl={3}>
          <Kpi
            title="Costo por transacción"
            value={formatUsd(cost.cost_per_transaction_usd)}
            badge={
              <Badge color={overTarget ? 'soft-danger' : 'soft-success'} pill className="fs--2 text-left" style={{ whiteSpace: 'normal' }}>
                {overTarget ? 'Sobre el' : 'Dentro del'} objetivo de {formatUsd(cost.target_cost_per_transaction_usd)}
              </Badge>
            }
          />
        </Col>
      </Row>

      <CoverageCard liability={liability} coins={coins} funds={funds} />

      <Row className="mb-3">
        <Col lg={4} className="mb-3 mb-lg-0">
          <DetailCard
            title="Ingresos"
            rows={[
              ['Recaudación procesada', formatUsd(revenue.collection_usd)],
              ['Pagos aprobados', formatNumber(revenue.transactions)],
              ['Comisión de pasarela', formatUsd(revenue.gateway_commission_usd), true],
              ['Comisión de afiliados esperada', formatUsd(revenue.affiliate_commission_usd)],
              ['Eventos de aceleradores', formatNumber(revenue.affiliate_events)]
            ]}
          />
        </Col>
        <Col lg={4} className="mb-3 mb-lg-0">
          <DetailCard
            title="Fondos"
            rows={[
              ['Aportes al Fondo de Puntos', formatUsd(funds.points_contributions_usd)],
              ['Salidas del Fondo de Puntos', formatUsd(funds.points_withdrawals_usd)],
              ['Aportes al Fondo de Premios', formatUsd(funds.prize_contributions_usd)],
              ['Premios entregados', formatUsd(funds.prizes_paid_usd)],
              ['Saldo del Fondo de Puntos', formatUsd(funds.points_balance_usd), true],
              ['Saldo del Fondo de Premios', formatUsd(funds.prize_balance_usd), true]
            ]}
          />
        </Col>
        <Col lg={4}>
          <DetailCard
            title="Homelan Coins"
            rows={[
              ['Emitidas', formatHc(coins.hc_issued), true],
              ['Canjeadas', formatHc(coins.hc_redeemed)],
              ['Revertidas', formatHc(coins.hc_reversed)],
              ['En circulación (hoy)', formatHc(coins.hc_in_circulation)],
              ['XP emitidos', `${formatNumber(coins.xp_issued, 2)} XP`]
            ]}
          />
        </Col>
      </Row>

      <ReconciliationCard reconciliation={reconciliation} />

      {!raffles?.available && (
        <Alert color="secondary" className="fs--1">
          <b>Sorteos:</b> el detalle auditable (semilla, tickets, ganadores y montos) todavía no está disponible
          porque depende del motor de sorteo (HO-TASK-273 y HO-TASK-274), que está en el backlog. Premios entregados
          en el período: {formatUsd(raffles?.prizes_paid_usd)}.
        </Alert>
      )}

      <p className="fs--2 text-500 mb-3">
        Los totales usan la fecha en que se registró cada movimiento, para que cuadren con los ledgers. Los saldos y
        la cobertura son a la fecha de hoy.
      </p>
    </>
  )
}

ReportSummary.propTypes = { report: PropTypes.object.isRequired }

export default ReportSummary
